import React, { useState } from 'react';
import { FiDatabase, FiServer, FiCheckCircle } from 'react-icons/fi';
import { message } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import Step2_PatientAndPregnancy from '../NewCaseReverseUpload/steps/Step2_PatientAndPregnancy';
import WorklistSearch from '../WorklistSearch/WorklistSearch';

import { Patient } from '../../../interfaces/patient';
import { EnrichedWorklistItem } from '../../../helpers/api/dicom/dicom';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  wizardState: any;
  setWizardState: React.Dispatch<React.SetStateAction<any>>;
  onConfirm: () => void;
  onDirectSaveFromPregnancy?: (worklistItem: any, pregnancyData: any) => void;
  isPatientLocked?: boolean;
}

type TabType = 'local' | 'worklist';

const PatientSelectionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  wizardState,
  setWizardState,
  onConfirm,
  onDirectSaveFromPregnancy,
  isPatientLocked = false,
}) => {
  const { t } = useTranslation('PatientSelectionModal');
  const [activeTab, setActiveTab] = useState<TabType>('local');
  const [selectedWorklistItem, setSelectedWorklistItem] =
    useState<EnrichedWorklistItem | null>(null);

  const [localView, setLocalView] = useState<'search' | 'add'>('search');
  const [isAddFormValid, setIsAddFormValid] = useState(false);

  const handleWorklistItemSelect = (item: EnrichedWorklistItem) => {
    setSelectedWorklistItem(item);

    const mwl = item.mwl || {};

    const localPregnancy = (item as any).localPregnancy;

    const patientFromWorklist: Partial<Patient> = {
      patient_name: mwl.PatientName || '',
      patient_id: mwl.PatientID || '',
      patient_birth_date: mwl.PatientBirthDate
        ? dayjs(mwl.PatientBirthDate, 'YYYYMMDD').format('YYYY-MM-DD')
        : '',
      patient_sex: mwl.PatientSex || '',
    };

    let pregnancyData = null;
    if (localPregnancy) {
      pregnancyData = {
        lmp: localPregnancy.lmp || null,
        edd: localPregnancy.edd || null,
        gaWeeks: localPregnancy.gaWeeks || null,
        gaDays: localPregnancy.gaDays || null,
        ultrasoundDate: null,
        crl: localPregnancy.crl ? parseFloat(localPregnancy.crl) : null,
        bpd: localPregnancy.bpd ? parseFloat(localPregnancy.bpd) : null,
        hc: localPregnancy.hc ? parseFloat(localPregnancy.hc) : null,
        ac: localPregnancy.ac ? parseFloat(localPregnancy.ac) : null,
        fl: localPregnancy.fl ? parseFloat(localPregnancy.fl) : null,
        isFromWorklist: true,
      };
    } else if (
      item.resolution?.active_pregnancy_exists &&
      item.resolution?.pregnancy
    ) {
      const rp = item.resolution.pregnancy;
      let gaWeeks: number | null = null;
      let gaDays: number | null = null;
      if (rp.last_menstrual_period) {
        const diffDays = dayjs().diff(dayjs(rp.last_menstrual_period), 'day');
        if (diffDays >= 0) {
          gaWeeks = Math.floor(diffDays / 7);
          gaDays = diffDays % 7;
        }
      }
      pregnancyData = {
        id: rp.id,
        patient_id: rp.patient_id,
        pregnancy_status: rp.pregnancy_status,
        dating_method: rp.dating_method,
        last_menstrual_period: rp.last_menstrual_period,
        estimated_due_date: rp.estimated_due_date,
        ultrasound_date: rp.ultrasound_date,
        gaWeeks,
        gaDays,
        preg_bpd: rp.preg_bpd,
        preg_hc: rp.preg_hc,
        preg_ac: rp.preg_ac,
        preg_fl: rp.preg_fl,
        preg_crl: rp.preg_crl,
        is_medically_active: rp.is_medically_active,
        existsInBackend: true,
        isFromWorklist: true,
      };
    }

    setWizardState((prev) => ({
      ...prev,
      worklistItem: item,
      worklistPatientData: patientFromWorklist,
      worklistPregnancy: pregnancyData,
    }));
  };

  const handleConfirm = () => {
    if (activeTab === 'worklist' && selectedWorklistItem) {
      if (!wizardState.worklistPregnancy) {
        message.warning(
          t('Please add pregnancy information for the selected worklist patient.')
        );
        return;
      }
      onConfirm();
      return;
    }

    if (activeTab === 'local' && localView === 'add') {
      message.info(
        t(
          'Please use the "Confirm & Save Views" button above to create the patient and save views.'
        )
      );
      return;
    }

    if (!wizardState.patient || !wizardState.pregnancy) {
      message.warning(
        t('Please select a patient and create/confirm pregnancy record.')
      );
      return;
    }

    onConfirm();
  };

  const isConfirmDisabled = () => {
    if (activeTab === 'local') {
      if (localView === 'add') {
        return !isAddFormValid;
      }
      return !wizardState.patient || !wizardState.pregnancy;
    }
    if (activeTab === 'worklist') {
      return !selectedWorklistItem || !wizardState.worklistPregnancy;
    }
    return false;
  };

  const handleLocalViewChange = (view: 'search' | 'add') => {
    setLocalView(view);
  };

  const handleFormValidityChange = (isValid: boolean) => {
    setIsAddFormValid(isValid);
  };

  const handleSaveAndConfirmFromAdd = () => {};

  const handleConfirmWithPregnancy = (pregnancyData: any) => {
    if (!selectedWorklistItem) return;

    setWizardState((prev) => ({
      ...prev,
      worklistPregnancy: {
        lmp: pregnancyData.lmp || null,
        edd: pregnancyData.edd || null,
        gaWeeks: pregnancyData.gaWeeks || null,
        gaDays: pregnancyData.gaDays || null,
        ultrasoundDate: null,
        crl: pregnancyData.crl ? parseFloat(pregnancyData.crl) : null,
        bpd: pregnancyData.bpd ? parseFloat(pregnancyData.bpd) : null,
        hc: pregnancyData.hc ? parseFloat(pregnancyData.hc) : null,
        ac: pregnancyData.ac ? parseFloat(pregnancyData.ac) : null,
        fl: pregnancyData.fl ? parseFloat(pregnancyData.fl) : null,
        isFromWorklist: true,
      },
    }));

    setTimeout(() => {
      onConfirm();
    }, 100);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <div className="flex flex-col h-full">
        {/* Header with Tabs */}
        <div className="border-b border-slate-200 dark:border-gray-700">
          <div className="px-6 pt-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {t('Patient & Pregnancy Selection')}
            </h2>
          </div>
          <div className="flex px-6">
            <button
              onClick={() => setActiveTab('local')}
              className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-colors relative ${
                activeTab === 'local'
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              }`}
            >
              <FiDatabase className="text-lg" />
              {t('Local Database')}
              {wizardState.patient && activeTab !== 'local' && (
                <FiCheckCircle className="text-green-600 text-sm" />
              )}
              {activeTab === 'local' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('worklist')}
              className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-colors relative ${
                activeTab === 'worklist'
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              }`}
            >
              <FiServer className="text-lg" />
              {t('Modality Worklist')}
              {selectedWorklistItem && activeTab !== 'worklist' && (
                <FiCheckCircle className="text-green-600 text-sm" />
              )}
              {activeTab === 'worklist' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />
              )}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'local' ? (
            <Step2_PatientAndPregnancy
              wizardState={wizardState}
              setWizardState={setWizardState}
              isPatientLocked={isPatientLocked}
              onDirectSaveFromPregnancy={onDirectSaveFromPregnancy}
              onViewChange={handleLocalViewChange}
              onFormValidityChange={handleFormValidityChange}
              onSaveAndConfirmViews={handleSaveAndConfirmFromAdd}
            />
          ) : (
            <WorklistSearch
              onWorklistItemSelect={handleWorklistItemSelect}
              selectedItem={selectedWorklistItem}
              onConfirmWithPregnancy={handleConfirmWithPregnancy}
              onDirectSaveFromPregnancy={onDirectSaveFromPregnancy}
            />
          )}
        </div>

        {/* Footer */}
        <Modal.Footer className="flex-shrink-0">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {activeTab === 'local' ? (
                localView === 'add' ? (
                  isAddFormValid ? (
                    <span className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                      <FiCheckCircle />
                      {t('Form ready • Use "Confirm & Save Views" button above')}
                    </span>
                  ) : (
                    t('Fill in the patient and pregnancy details')
                  )
                ) : wizardState.patient ? (
                  <span className="flex items-center gap-2 text-green-600 dark:text-green-400">
                    <FiCheckCircle />
                    {t('Patient: {{name}}', { name: wizardState.patient.patient_name })}
                    {wizardState.pregnancy && ` ${t('• Pregnancy record confirmed')}`}
                  </span>
                ) : (
                  t('Select or create a patient to continue')
                )
              ) : selectedWorklistItem ? (
                <span className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                  <FiCheckCircle />
                  {t('Worklist: {{name}} ({{id}})', {
                    name: selectedWorklistItem.mwl?.PatientName,
                    id: selectedWorklistItem.mwl?.PatientID,
                  })}
                  {wizardState.worklistPregnancy && ` ${t('• Pregnancy info added')}`}
                </span>
              ) : (
                t('Search and select from modality worklist')
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={onClose}>
                {t('Cancel')}
              </Button>
              {/* Only show Confirm & Save Views button when not in add view */}
              {!(activeTab === 'local' && localView === 'add') && (
                <Button
                  variant="primary"
                  onClick={handleConfirm}
                  disabled={isConfirmDisabled()}
                >
                  {t('Confirm & Save Views')}
                </Button>
              )}
            </div>
          </div>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default PatientSelectionModal;
